import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Plus, X } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";

const STATE_STYLE = {
  "Novo": "bg-gray-100 text-gray-700 border-gray-300",
  "Contactado": "bg-blue-50 text-blue-800 border-blue-300",
  "Follow-up": "bg-amber-50 text-amber-800 border-amber-300",
  "Cotação enviada": "bg-cyan-50 text-cyan-800 border-cyan-300",
  "Negociação": "bg-indigo-50 text-indigo-800 border-indigo-300",
  "Ganho": "bg-emerald-50 text-emerald-800 border-emerald-300",
  "Perdido": "bg-red-50 text-red-800 border-red-300",
  "Fechado": "bg-gray-200 text-gray-700 border-gray-300",
};

const TIER_STYLE = {
  Hot: "bg-[#DC3545] text-white",
  Warm: "bg-[#F59E0B] text-white",
  Cold: "bg-gray-200 text-gray-700",
};

export default function OpportunitiesPage() {
  const [rows, setRows] = useState([]);
  const [enums, setEnums] = useState(null);
  const [filter, setFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [{ data }, { data: e }] = await Promise.all([
      api.get("/crm/opportunities" + (filter ? `?state=${encodeURIComponent(filter)}` : "")),
      api.get("/crm/meta/enums"),
    ]);
    setRows(data);
    setEnums(e);
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2"><Target className="w-6 h-6 text-[#1F3864]" /> Oportunidades (CRM)</h1>
          <p className="text-sm text-[#6C757D] mt-1">Pipeline comercial: leads, oportunidades, ganhos e perdidos.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="opp-new-btn"
          className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-2 rounded-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nova oportunidade
        </button>
      </div>

      {/* State filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="opp-filter-bar">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-sm border ${filter === "" ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#1F3864]"}`}>Todas</button>
        {enums?.opportunity_states?.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-sm border ${filter === s ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#1F3864]"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">#</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Cliente / Lead</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Marca</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Valor</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Lead score</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Estado</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Fonte</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Criado</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-[#6C757D] italic px-3 py-6">Sem oportunidades.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-[#FAFBFC]" data-testid={`opp-row-${r.number}`}>
                <td className="px-3 py-2 font-mono text-[#1F3864] font-semibold">{r.number}</td>
                <td className="px-3 py-2">{r.customer?.name || r.lead_name || "—"}</td>
                <td className="px-3 py-2">{r.brand || "—"}</td>
                <td className="px-3 py-2">{r.expected_amount ? r.expected_amount.toLocaleString() + " MZN" : "—"}</td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] uppercase font-bold rounded-sm px-2 py-0.5 ${TIER_STYLE[r.lead_tier] || "bg-gray-200"}`} data-testid={`opp-tier-${r.number}`}>
                    {r.lead_tier} · {r.lead_score}
                  </span>
                </td>
                <td className="px-3 py-2"><span className={`text-[11px] uppercase font-semibold border rounded-sm px-2 py-0.5 ${STATE_STYLE[r.current_state] || "bg-gray-100"}`}>{r.current_state}</span></td>
                <td className="px-3 py-2 text-xs">{r.lead_source}</td>
                <td className="px-3 py-2 text-xs text-[#6C757D]">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && enums && <NewOppDialog enums={enums} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOppDialog({ enums, onClose, onSaved }) {
  const [form, setForm] = useState({
    lead_name: "", lead_phone: "", lead_source: "Walk-in", brand: "", expected_amount: "",
  });
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/customers?limit=100").then((r) => setCustomers(r.data)); }, []);

  const submit = async () => {
    try {
      const payload = {
        customer_id: customerId || undefined,
        lead_name: form.lead_name || undefined,
        lead_phone: form.lead_phone || undefined,
        lead_source: form.lead_source,
        brand: form.brand || undefined,
        expected_amount: form.expected_amount ? Number(form.expected_amount) : undefined,
      };
      await api.post("/crm/opportunities", payload);
      onSaved();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="opp-new-dialog">
      <div className="bg-white rounded-md border border-gray-300 w-full max-w-lg">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#1A1A2E]">Nova oportunidade</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#6C757D]" /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}
          <Field label="Cliente existente (opcional)">
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="opp-customer-select">
              <option value="">— (lead novo) —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.name}</option>)}
            </select>
          </Field>
          {!customerId && (
            <>
              <Field label="Nome do lead *"><input value={form.lead_name} onChange={(e) => setForm({ ...form, lead_name: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="opp-lead-name" /></Field>
              <Field label="Telefone *"><input value={form.lead_phone} onChange={(e) => setForm({ ...form, lead_phone: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="opp-lead-phone" /></Field>
            </>
          )}
          <Field label="Fonte">
            <select value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm">
              {enums.lead_sources.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Marca de interesse"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" /></Field>
          <Field label="Valor esperado (MZN)"><input type="number" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm font-mono" /></Field>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-4 py-1.5 rounded-sm" data-testid="opp-save-btn">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span><div className="mt-0.5">{children}</div></label>;
}
